import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Box,
  Typography,
  TablePagination,
} from "@mui/material";
import { nikshayAPI } from "../../../services/nikshay.api";

const statusConfig = {
  "Results Available": { label: "Results Available", color: "success" },
  "Sample Collection Pending": { label: "Sample Pending", color: "warning" },
  "Test Pending": { label: "Test Pending", color: "warning" },
};

const getStatusChip = (status) => {
  const config = statusConfig[status] || { label: status || "Unknown", color: "default" };
  return (
    <Chip
      label={config.label}
      color={config.color}
      size="small"
      variant="outlined"
    />
  );
};

const NikshayTestTab = ({ onClose, setShowNikshayActions, patientId, activeTab, refId }) => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Step 2: When user clicks Test tab — call getDataAgainByRefId using the stored refId
  useEffect(() => {
    console.log("[NikshayTestTab] activeTab:", activeTab, "refId:", refId);
    if (activeTab !== "test" || !refId) return;

    console.log("[NikshayTestTab] Calling getDataAgainByRefId with refId:", refId);
    const fetchTestData = async () => {
      setLoading(true);
      setError(null);
      try {
        const refRes = await nikshayAPI.getDataAgainByRefId(refId);
        const rawResponse = refRes?.data?.json_received?.Data?.Response;

        if (!rawResponse) {
          setTests([]);
          return;
        }

        const parsed = typeof rawResponse === "string" ? JSON.parse(rawResponse) : rawResponse;
        setTests(Array.isArray(parsed) ? parsed : []);
        setPage(0); // reset to first page on fresh data
      } catch (err) {
        console.error("Error fetching test data:", err);
        setError("Failed to load test data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchTestData();
  }, [activeTab, refId]);

  return (
    <div className="nikshay-wrapper">
      {/* CARD - TEST TABLE */}
      <div className="nikshay-card">
        <div className="d-flex justify-content-between align-items-center mb-3">
          {/* <h5 className="mb-0">Tests</h5> */}
          <button
            className="search-btn-abha"
            onClick={() => setShowNikshayActions("Add Nikshay Test")}
          >
            Add Test
          </button>
        </div>

        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress size={32} />
            <Typography variant="body2" ml={2} color="text.secondary">
              Loading tests...
            </Typography>
          </Box>
        )}

        {error && !loading && (
          <Box py={3} textAlign="center">
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          </Box>
        )}

        {!loading && !error && (
          <>
          <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}>
            <Table size="small" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>Test ID</TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>Requesting Facility</TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>Request Date</TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>Test Type</TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>Testing Facility</TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>Reason for Testing</TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>Final Interpretation</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      No Nikshay tests recorded for this patient.
                    </TableCell>
                  </TableRow>
                ) : (
                  tests
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((test, index) => (
                      <TableRow
                        key={test.RequestId ?? (page * rowsPerPage + index)}
                        hover
                        sx={{ "&:last-child td": { borderBottom: 0 } }}
                      >
                        <TableCell>{test.RequestId ?? "N/A"}</TableCell>
                        <TableCell>{test.RequestFacility ?? "N/A"}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{test.RequestDate ?? "N/A"}</TableCell>
                        <TableCell>{test.TestType ?? "N/A"}</TableCell>
                        <TableCell>{test.TestingFacility?.trim() || "N/A"}</TableCell>
                        <TableCell>{test.Reason ?? "N/A"}</TableCell>
                        <TableCell>{getStatusChip(test.Status)}</TableCell>
                        <TableCell>{test.FinalInterpretation ?? "N/A"}</TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={tests.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              borderTop: "1px solid #e0e0e0",
              "& .MuiTablePagination-toolbar": {
                display: "flex",
                alignItems: "center",
                minHeight: 48,
              },
              "& .MuiTablePagination-selectLabel": {
                margin: 0,
                lineHeight: "normal",
              },
              "& .MuiTablePagination-displayedRows": {
                margin: 0,
                lineHeight: "normal",
              },
            }}
          />
          </>
        )}
      </div>
    </div>
  );
};

export default NikshayTestTab;
